import React, { useState } from "react";
import { Link } from "react-router-dom";
import styles from "./ExamInfo.module.css";

// ─── Dynamic year helpers ───────────────────────────────────────────────────
const currentYear = new Date().getFullYear();
const nextYear    = currentYear + 1;
const academicYear = `${currentYear}-${String(nextYear).slice(-2)}`;

// ─── Data ───────────────────────────────────────────────────────────────────
const exams = [
  {
    id: "jee-mains",
    tag: "Engineering",
    color: "#2563eb",
    gradient: "linear-gradient(135deg,#1e40af 0%,#3b82f6 100%)",
    icon: "⚙️",
    title: "JEE Mains",
    subtitle: `Joint Entrance Examination (Main) ${currentYear}`,
    conducted: "National Testing Agency (NTA)",
    shortDesc: "Gateway to NITs, IIITs, GFTIs and eligibility for JEE Advanced. One of India's most competitive engineering entrance exams.",
    keyFacts: [
      { icon: "📅", label: "Sessions", value: "January & April" },
      { icon: "🕐", label: "Duration", value: "3 Hours" },
      { icon: "📝", label: "Mode", value: "Computer Based Test (CBT)" },
      { icon: "🌐", label: "Languages", value: "13 Languages" },
      { icon: "🎓", label: "Eligibility", value: `Class 12 Pass / Appearing (${academicYear})` },
      { icon: "🔄", label: "Attempts", value: "Max 3 consecutive years" },
    ],
    pattern: {
      papers: [
        {
          name: "Paper 1 — B.E. / B.Tech",
          sections: [
            { subject: "Physics", mcq: 20, numerical: 5, marks: "100 (+4/−1 for MCQ, +4/0 for Numerical)" },
            { subject: "Chemistry", mcq: 20, numerical: 5, marks: "100 (+4/−1 for MCQ, +4/0 for Numerical)" },
            { subject: "Maths", mcq: 20, numerical: 5, marks: "100 (+4/−1 for MCQ, +4/0 for Numerical)" },
          ],
          total: "300 Marks | 90 Questions",
        },
        {
          name: "Paper 2A — B.Arch",
          sections: [
            { subject: "Mathematics", mcq: 20, numerical: 5, marks: "100" },
            { subject: "Aptitude Test", mcq: 50, numerical: 0, marks: "200" },
            { subject: "Drawing Test", mcq: 0, numerical: 2, marks: "100 (offline)" },
          ],
          total: "400 Marks | 77 Questions",
        },
      ],
      markingScheme: "+4 for correct, −1 for wrong MCQ, 0 for unattempted. Numerical questions have no negative marking.",
      normalization: "Percentile scores calculated separately for each session. Best of two sessions considered.",
    },
    syllabus: {
      Physics: ["Mechanics", "Thermodynamics", "Electro-dynamics", "Modern Physics", "Optics", "Waves & Sound"],
      Chemistry: ["Physical Chemistry", "Organic Chemistry", "Inorganic Chemistry", "Environmental Chemistry"],
      Mathematics: ["Algebra", "Calculus", "Coordinate Geometry", "Trigonometry", "Statistics & Probability", "Vectors & 3D"],
    },
    importantDates: [
      { event: "Session 1 Registration", date: `Nov ${currentYear}` },
      { event: "Session 1 Exam", date: `Jan ${nextYear}` },
      { event: "Session 2 Registration", date: `Feb ${nextYear}` },
      { event: "Session 2 Exam", date: `Apr ${nextYear}` },
      { event: "Result Declaration", date: `May ${nextYear}` },
    ],
    cutoff: "Typically top ~2.5 lakh students qualify for JEE Advanced.",
    officialSite: "https://jeemain.nta.ac.in",
    syllabusLink: "https://www.prrepxpathshala.live/iitjee-syllabus",
    stats: [
      { num: "~11L+", lbl: "Aspirants / Year" },
      { num: "~31,000", lbl: "NIT/IIIT/GFTI Seats" },
      { num: "2×", lbl: "Sessions Per Year" },
      { num: academicYear, lbl: "Academic Cycle" },
    ],
  },
  {
    id: "jee-advanced",
    tag: "IIT Admission",
    color: "#7c3aed",
    gradient: "linear-gradient(135deg,#4c1d95 0%,#7c3aed 100%)",
    icon: "🏆",
    title: "JEE Advanced",
    subtitle: `Joint Entrance Examination (Advanced) ${nextYear}`,
    conducted: "IITs (on rotation)",
    shortDesc: "Admission to 23 IITs offering B.Tech, B.S., B.Arch and Integrated programs. Considered one of the toughest exams in the world.",
    keyFacts: [
      { icon: "📅", label: "Exam Date", value: `May/June ${nextYear}` },
      { icon: "🕐", label: "Duration", value: "3 Hours per paper (2 papers)" },
      { icon: "📝", label: "Mode", value: "Computer Based Test (CBT)" },
      { icon: "🎓", label: "Eligibility", value: "Top 2.5 lakh JEE Mains qualifiers" },
      { icon: "🔄", label: "Attempts", value: "Max 2 consecutive attempts" },
      { icon: "🏫", label: "Institutes", value: "23 IITs across India" },
    ],
    pattern: {
      papers: [
        {
          name: "Paper 1 (3 Hours)",
          sections: [
            { subject: "Physics", mcq: "~18", numerical: "~4", marks: "~60" },
            { subject: "Chemistry", mcq: "~18", numerical: "~4", marks: "~60" },
            { subject: "Maths", mcq: "~18", numerical: "~4", marks: "~60" },
          ],
          total: "~180 Marks | Multiple question types",
        },
        {
          name: "Paper 2 (3 Hours)",
          sections: [
            { subject: "Physics", mcq: "~18", numerical: "~4", marks: "~60" },
            { subject: "Chemistry", mcq: "~18", numerical: "~4", marks: "~60" },
            { subject: "Maths", mcq: "~18", numerical: "~4", marks: "~60" },
          ],
          total: "~180 Marks | Multiple question types",
        },
      ],
      markingScheme: "Varies by question type: Single correct (+3/−1), Multiple correct (+4/partial), Numerical (+3/0). Both papers are mandatory.",
      normalization: "Aggregate of Paper 1 + Paper 2 for final ranking. No normalization; direct marks used.",
    },
    syllabus: {
      Physics: ["Mechanics (advanced)", "Waves & Optics", "Electricity & Magnetism", "Modern Physics", "Thermal Physics"],
      Chemistry: ["Organic Synthesis", "Physical Equilibria", "Electrochemistry", "Coordination Compounds", "Thermodynamics"],
      Mathematics: ["Complex Numbers", "Matrices & Determinants", "Integral Calculus", "Differential Equations", "Probability"],
    },
    importantDates: [
      { event: "Registration Opens", date: `Apr ${nextYear}` },
      { event: "Admit Card", date: `May ${nextYear}` },
      { event: "Paper 1 & 2", date: `May ${nextYear}` },
      { event: "Answer Key Release", date: `June ${nextYear}` },
      { event: "Result Declaration", date: `June ${nextYear}` },
    ],
    cutoff: `Category-wise cutoffs vary. General category typically needs ~35–40% aggregate. Ranks released for top ~40,000 students.`,
    officialSite: "https://jeeadv.ac.in",
    syllabusLink: "https://www.prrepxpathshala.live/iitjee-syllabus",
    stats: [
      { num: "~2.5L", lbl: "Qualify from JEE Mains" },
      { num: "~17,000", lbl: "IIT Seats" },
      { num: "23", lbl: "IITs across India" },
      { num: academicYear, lbl: "Academic Cycle" },
    ],
  },
  {
    id: "neet",
    tag: "Medical",
    color: "#059669",
    gradient: "linear-gradient(135deg,#064e3b 0%,#059669 100%)",
    icon: "🩺",
    title: "NEET UG",
    subtitle: `National Eligibility cum Entrance Test (UG) ${nextYear}`,
    conducted: "National Testing Agency (NTA)",
    shortDesc: "Single national entrance test for admission to MBBS, BDS, BAMS, BSMS, BUMS, BHMS and BSc Nursing courses across India.",
    keyFacts: [
      { icon: "📅", label: "Exam Date", value: `May ${nextYear} (1st Sunday)` },
      { icon: "🕐", label: "Duration", value: "3 Hours 20 Minutes" },
      { icon: "📝", label: "Mode", value: "Pen & Paper (OMR Based)" },
      { icon: "🌐", label: "Languages", value: "13 Languages" },
      { icon: "🎓", label: "Eligibility", value: `Class 12 with PCB (min 50%). Age 17+ by Dec 31 ${nextYear}` },
      { icon: "🔄", label: "Attempts", value: "No limit (as per current rules)" },
    ],
    pattern: {
      papers: [
        {
          name: "NEET UG Single Paper",
          sections: [
            { subject: "Physics (Sec A)", mcq: 35, numerical: 0, marks: "140" },
            { subject: "Physics (Sec B)", mcq: 15, numerical: 0, marks: "40 (attempt any 10)" },
            { subject: "Chemistry (Sec A)", mcq: 35, numerical: 0, marks: "140" },
            { subject: "Chemistry (Sec B)", mcq: 15, numerical: 0, marks: "40 (attempt any 10)" },
            { subject: "Botany (Sec A)", mcq: 35, numerical: 0, marks: "140" },
            { subject: "Botany (Sec B)", mcq: 15, numerical: 0, marks: "40 (attempt any 10)" },
            { subject: "Zoology (Sec A)", mcq: 35, numerical: 0, marks: "140" },
            { subject: "Zoology (Sec B)", mcq: 15, numerical: 0, marks: "40 (attempt any 10)" },
          ],
          total: "720 Marks | 200 Questions (180 to attempt)",
        },
      ],
      markingScheme: "+4 for correct answer, −1 for wrong answer, 0 for unattempted.",
      normalization: "No normalization. Single exam, direct score used for all-India merit list.",
    },
    syllabus: {
      Physics: ["Mechanics", "Thermodynamics", "Electrostatics", "Current Electricity", "Optics", "Modern Physics", "Waves"],
      Chemistry: ["Basic Concepts", "Organic Chemistry", "Inorganic Chemistry", "Physical Chemistry", "Biomolecules"],
      Biology: ["Cell Biology", "Genetics & Evolution", "Human Physiology", "Plant Physiology", "Ecology", "Biotechnology", "Reproduction"],
    },
    importantDates: [
      { event: "Registration Opens", date: `Feb ${nextYear}` },
      { event: "Last Date to Apply", date: `Mar ${nextYear}` },
      { event: "Admit Card Download", date: `May ${nextYear}` },
      { event: "Exam Date", date: `May ${nextYear} (1st Sunday)` },
      { event: "Result Declaration", date: `June ${nextYear}` },
    ],
    cutoff: "General category cutoff typically ~720–130. For MBBS govt colleges expect 550+. State quotas have separate cutoffs.",
    officialSite: "https://neet.nta.nic.in",
    syllabusLink: "https://www.prrepxpathshala.live/neet-syllabus",
    stats: [
      { num: "~22L+", lbl: "Aspirants / Year" },
      { num: "~1.08L", lbl: "MBBS Seats" },
      { num: "1×", lbl: "Session Per Year" },
      { num: academicYear, lbl: "Academic Cycle" },
    ],
  },
];

const comparisonData = [
  { param: "Conducting Body", jeeMain: "NTA", jeeAdv: "IITs", neet: "NTA" },
  { param: "Exam Mode", jeeMain: "CBT (Online)", jeeAdv: "CBT (Online)", neet: "OMR (Offline)" },
  { param: "Frequency", jeeMain: "Twice a year", jeeAdv: "Once a year", neet: "Once a year" },
  { param: "Duration", jeeMain: "3 Hours", jeeAdv: "3+3 Hours", neet: "3 Hr 20 Min" },
  { param: "Total Marks", jeeMain: "300", jeeAdv: "~360", neet: "720" },
  { param: "Negative Marking", jeeMain: "−1 (MCQ)", jeeAdv: "Varies (−1 to −2)", neet: "−1" },
  { param: "Admission To", jeeMain: "NITs, IIITs, GFTIs", jeeAdv: "IITs", neet: "MBBS, BDS, BAMS etc." },
  { param: `Seats (Approx ${nextYear})`, jeeMain: "~31,000", jeeAdv: "~17,000", neet: "~1,08,000 MBBS" },
];

// ─── Component ───────────────────────────────────────────────────────────────
export default function ExamInfo() {
  const [activeExam, setActiveExam] = useState("jee-mains");
  const [activeTab, setActiveTab] = useState("overview");
  const [sidebarOpen, setSidebarOpen] = useState(false);

  const closeSidebar = () => setSidebarOpen(false);
  const openTab = (t) => { setActiveTab(t); closeSidebar(); };
  const openExam = (id) => { setActiveExam(id); setActiveTab("overview"); closeSidebar(); };

  const exam = exams.find(e => e.id === activeExam);
  const tabs = ["overview", "pattern", "syllabus", "dates", "compare", "tips"];

  return (
    <div className={styles.page}>

      {/* Mobile sidebar backdrop */}
      {sidebarOpen && (
        <div className={styles.sidebarBackdrop} onClick={closeSidebar} />
      )}

      {/* ── MINIMAL TOPBAR ────────────────────────────────── */}
      <div className={styles.topBar}>
        <div className={styles.topBarInner}>
          <div className={styles.topBarLeft}>
            {/* Three-dots menu — mobile only */}
            <button className={styles.menuDots} onClick={() => setSidebarOpen(o => !o)} aria-label="Open menu">
              <span /><span /><span />
            </button>
            <span className={styles.topBarIcon}>📚</span>
            <span className={styles.topBarTitle}>Exam Guide</span>
            <span className={styles.topBarSep}>/</span>
            <span className={styles.topBarSub}>JEE &amp; NEET {academicYear}</span>
          </div>
          <div className={styles.topBarExams}>
            {exams.map(e => (
              <button key={e.id}
                className={`${styles.topBarExamBtn} ${activeExam === e.id ? styles.topBarExamBtnActive : ""}`}
                style={{ "--exam-color": e.color }}
                onClick={() => openExam(e.id)}>
                <span className={styles.topBarExamIcon}>{e.icon}</span>
                <span className={styles.topBarExamMeta}>
                  <span className={styles.topBarExamName}>{e.title}</span>
                  <span className={styles.topBarExamTag}>{e.tag}</span>
                </span>
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* ── LAYOUT ─────────────────────────────────────────── */}
      <div className={styles.layout}>

        {/* SIDEBAR — dark blue, section tabs only */}
        <aside className={`${styles.sidebar} ${sidebarOpen ? styles.sidebarMobileOpen : ""}`}>
          {/* Mobile drawer header */}
          <div className={styles.sidebarDrawerHeader}>
            <span className={styles.sidebarDrawerTitle}>📚 Navigation</span>
            <button className={styles.sidebarCloseBtn} onClick={closeSidebar} aria-label="Close menu">×</button>
          </div>
          {/* Mobile: exam selector inside drawer */}
          <div className={styles.sidebarDrawerExams}>
            <p className={styles.sidebarLabel}>Select Exam</p>
            {exams.map(e => (
              <button key={e.id}
                className={`${styles.sidebarDrawerExamBtn} ${activeExam === e.id ? styles.sidebarDrawerExamBtnActive : ""}`}
                style={{ "--exam-color": e.color }}
                onClick={() => openExam(e.id)}>
                <span>{e.icon}</span>
                <span className={styles.sidebarDrawerExamName}>{e.title}</span>
                <span className={styles.sidebarDrawerExamTag} style={{ background: e.color }}>{e.tag}</span>
              </button>
            ))}
          </div>
          <div className={styles.sidebarDivider} />
          <p className={styles.sidebarLabel}>Exam Info</p>
          <div className={styles.sidebarTabRow}>
            {["overview", "pattern", "syllabus", "dates"].map(t => (
              <button key={t}
                className={`${styles.sidebarTabBtn} ${activeTab === t ? styles.sidebarTabBtnActive : ""}`}
                style={{ "--exam-color": exam.color }}
                onClick={() => openTab(t)}>
                <span className={styles.sidebarTabIcon}>
                  {{ overview: "📋", pattern: "📊", syllabus: "📚", dates: "📅" }[t]}
                </span>
                <span className={styles.sidebarTabLabel}>
                  {{ overview: "Overview", pattern: "Exam Pattern", syllabus: "Syllabus", dates: "Dates & Cutoffs" }[t]}
                </span>
              </button>
            ))}
          </div>
          <div className={styles.sidebarDivider} />
          <p className={styles.sidebarLabel}>Guides</p>
          <div className={styles.sidebarTabRow}>
            {["compare", "tips"].map(t => (
              <button key={t}
                className={`${styles.sidebarTabBtn} ${activeTab === t ? styles.sidebarTabBtnActive : ""}`}
                style={{ "--exam-color": "#f59e0b" }}
                onClick={() => openTab(t)}>
                <span className={styles.sidebarTabIcon}>
                  {{ compare: "📊", tips: "💡" }[t]}
                </span>
                <span className={styles.sidebarTabLabel}>
                  {{ compare: "Compare", tips: "Prep Tips" }[t]}
                </span>
              </button>
            ))}
          </div>
        </aside>

        {/* MAIN CONTENT */}
        <main id="exam-detail" className={styles.mainContent}>

          {/* ── OVERVIEW ── */}
          {activeTab === "overview" && (
            <div className={styles.tabContent}>
              {/* Conducted-by info bar */}
              <div className={styles.conductedBar} style={{ "--exam-color": exam.color }}>
                <div className={styles.conductedLeft}>
                  <span className={styles.conductedIcon}>{exam.icon}</span>
                  <div>
                    <p className={styles.conductedSub}>{exam.tag} Entrance Exam</p>
                    <h2 className={styles.conductedTitle}>{exam.title}</h2>
                    <p className={styles.conductedSub2}>{exam.subtitle}</p>
                    <p className={styles.conductedDesc}>{exam.shortDesc}</p>
                  </div>
                </div>
                <div className={styles.conductedRight}>
                  <span className={styles.conductedByLabel}>Conducted by</span>
                  <strong className={styles.conductedOrg}>{exam.conducted}</strong>
                  <a href={exam.officialSite} target="_blank" rel="noopener noreferrer"
                    className={styles.officialBtn} style={{ "--exam-color": exam.color }}>
                    🌐 Official Website
                  </a>
                </div>
              </div>
              {/* Exam-specific stat strip */}
              {exam.stats && (
                <div className={styles.overviewStats} style={{ "--exam-color": exam.color }}>
                  {exam.stats.map((s, i) => (
                    <React.Fragment key={s.lbl}>
                      <div className={styles.ovStat}>
                        <span className={styles.ovStatNum} style={{ color: exam.color }}>{s.num}</span>
                        <span className={styles.ovStatLbl}>{s.lbl}</span>
                      </div>
                      {i < exam.stats.length - 1 && <div className={styles.ovStatDivider} />}
                    </React.Fragment>
                  ))}
                </div>
              )}
              <div className={styles.tableWrapper}>
                <table className={styles.keyFactsTable}>
                  <thead>
                    <tr>
                      {exam.keyFacts.map(f => (
                        <th key={f.label} className={styles.kfColHead} style={{ color: exam.color }}>
                          <span className={styles.kfColIcon}>{f.icon}</span>
                          <span>{f.label}</span>
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    <tr>
                      {exam.keyFacts.map(f => (
                        <td key={f.label} className={styles.kfColValue}>{f.value}</td>
                      ))}
                    </tr>
                  </tbody>
                </table>
              </div>
              <div className={styles.markingBox} style={{ borderColor: exam.color }}>
                <h4 className={styles.markingTitle} style={{ color: exam.color }}>⚡ Marking Scheme</h4>
                <p className={styles.markingText}>{exam.pattern.markingScheme}</p>
              </div>
              <div className={styles.normBox}>
                <h4 className={styles.normTitle}>🔢 Normalization / Score Policy</h4>
                <p className={styles.normText}>{exam.pattern.normalization}</p>
              </div>
              <div className={styles.cta}>
                <Link to="/test-series/pyq" className={styles.ctaBtn} style={{ background: exam.gradient }}>
                  🎯 Practice Free Mock Tests
                </Link>
                <a href={exam.officialSite} target="_blank" rel="noopener noreferrer" className={styles.ctaBtnOutline} style={{ color: exam.color, borderColor: exam.color }}>
                  📄 Official Notification
                </a>
              </div>
            </div>
          )}

          {/* ── PATTERN ── */}
          {activeTab === "pattern" && (
            <div className={styles.tabContent}>
              {exam.pattern.papers.map(paper => (
                <div key={paper.name} className={styles.paperCard}>
                  <div className={styles.paperHeader} style={{ background: exam.gradient }}>
                    <h3 className={styles.paperName}>{paper.name}</h3>
                    <span className={styles.paperTotal}>{paper.total}</span>
                  </div>
                  <div className={styles.tableWrapper}>
                    <table className={styles.patternTable}>
                      <thead>
                        <tr>
                          <th>Subject</th>
                          <th>MCQ / Questions</th>
                          <th>Integer / Numerical</th>
                          <th>Marks</th>
                        </tr>
                      </thead>
                      <tbody>
                        {paper.sections.map(s => (
                          <tr key={s.subject}>
                            <td><strong>{s.subject}</strong></td>
                            <td>{s.mcq}</td>
                            <td>{s.numerical}</td>
                            <td>{s.marks}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              ))}
              <div className={styles.markingBox} style={{ borderColor: exam.color }}>
                <h4 className={styles.markingTitle} style={{ color: exam.color }}>⚡ Marking Scheme</h4>
                <p className={styles.markingText}>{exam.pattern.markingScheme}</p>
              </div>
            </div>
          )}

          {/* ── SYLLABUS ── */}
          {activeTab === "syllabus" && (
            <div className={styles.tabContent}>
              <p className={styles.syllabusNote}>
                📌 Syllabus aligned with NCERT Class 11 &amp; 12. Topics listed are indicative; always check the official notification for the latest syllabus.
              </p>
              <div className={styles.syllabusGrid}>
                {Object.entries(exam.syllabus).map(([subject, topics]) => (
                  <div key={subject} className={styles.syllabusCard} style={{ "--exam-color": exam.color }}>
                    <h3 className={styles.syllabusSubject} style={{ color: exam.color }}>{subject}</h3>
                    <ul className={styles.topicList}>
                      {topics.map(t => (
                        <li key={t} className={styles.topicItem}>
                          <span className={styles.topicDot} style={{ background: exam.color }} />
                          {t}
                        </li>
                      ))}
                    </ul>
                  </div>
                ))}
              </div>
              {exam.syllabusLink && (
                <div className={styles.syllabusLinkRow}>
                  <a
                    href={exam.syllabusLink}
                    target="_blank"
                    rel="noopener noreferrer"
                    className={styles.syllabusDetailBtn}
                    style={{ background: exam.gradient, "--exam-color": exam.color }}
                  >
                    📖 Click here for Detailed Syllabus
                  </a>
                </div>
              )}
            </div>
          )}

          {/* ── DATES ── */}
          {activeTab === "dates" && (
            <div className={styles.tabContent}>
              <div className={styles.approxBanner}>
                ⚠️ <strong>Note:</strong> All dates below are <strong>approximate</strong> and based on historical trends. Actual dates may vary. Always check the <a href={exam.officialSite} target="_blank" rel="noopener noreferrer" style={{ color: exam.color }}>official website</a> for confirmed dates.
              </div>
              <div className={styles.timelineWrapper}>
                {exam.importantDates.map((d, i) => (
                  <div key={i} className={styles.timelineItem} style={{ "--exam-color": exam.color }}>
                    <div className={styles.timelineDot} style={{ background: exam.gradient }} />
                    <div className={styles.timelineCard}>
                      <p className={styles.timelineEvent}>{d.event}</p>
                      <p className={styles.timelineDate} style={{ color: exam.color }}>{d.date}</p>
                    </div>
                  </div>
                ))}
              </div>
              <div className={styles.cutoffBox} style={{ borderColor: exam.color }}>
                <h4 className={styles.cutoffTitle} style={{ color: exam.color }}>🏅 Cutoff Trends</h4>
                <p className={styles.cutoffText}>{exam.cutoff}</p>
              </div>
              <p className={styles.dateDisclaimer}>
                * Dates are approximate based on historical trends for {academicYear}. Always verify on the official website.
              </p>
            </div>
          )}

          {/* ── COMPARE ── */}
          {activeTab === "compare" && (
            <div className={styles.tabContent}>
              <div className={styles.tabPageHeader}>
                <span className={styles.tabPageBadge}>📊 Side-by-Side</span>
                <h2 className={styles.tabPageTitle}>Exam Comparison — {academicYear}</h2>
                <p className={styles.tabPageSub}>All three major entrance exams at a glance</p>
              </div>
              <div className={styles.tableWrapper}>
                <table className={styles.compareTable}>
                  <thead>
                    <tr>
                      <th>Parameter</th>
                      <th style={{ color: "#2563eb" }}>⚙️ JEE Mains</th>
                      <th style={{ color: "#7c3aed" }}>🏆 JEE Advanced</th>
                      <th style={{ color: "#059669" }}>🩺 NEET UG</th>
                    </tr>
                  </thead>
                  <tbody>
                    {comparisonData.map((row, i) => (
                      <tr key={i} className={i % 2 === 0 ? styles.rowEven : ""}>
                        <td><strong>{row.param}</strong></td>
                        <td>{row.jeeMain}</td>
                        <td>{row.jeeAdv}</td>
                        <td>{row.neet}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* ── TIPS ── */}
          {activeTab === "tips" && (
            <div className={styles.tabContent}>
              <div className={styles.tabPageHeader}>
                <span className={styles.tabPageBadge}>💡 Pro Tips</span>
                <h2 className={styles.tabPageTitle}>How to Prepare Smart</h2>
                <p className={styles.tabPageSub}>Strategies that top rankers swear by</p>
              </div>
              <div className={styles.tipsGrid}>
                {[
                  { icon: "📆", title: "Start Early", desc: `With exams in ${nextYear}, start your preparation in Class 11. Early starters have a massive edge.` },
                  { icon: "📝", title: "Mock Tests Are Key", desc: "Attempt full-length mock tests weekly. Analyse mistakes and focus on weak chapters." },
                  { icon: "📊", title: "NCERT First", desc: "Build all fundamentals from NCERT before moving to advanced books (HC Verma, MS Chauhan, etc.)." },
                  { icon: "🔄", title: "Revise Regularly", desc: "Keep a revision cycle. Every topic must be revised at least 3–4 times before the exam." },
                  { icon: "⏱️", title: "Time Management", desc: "Practice mock tests in exam conditions. Learn which questions to skip and which to attempt first." },
                  { icon: "🎯", title: "Focus on High-Weightage", desc: "Identify chapters with high marks weightage from PYQs and prioritise them in your schedule." },
                ].map(tip => (
                  <div key={tip.title} className={styles.tipCard}>
                    <span className={styles.tipIcon}>{tip.icon}</span>
                    <h4 className={styles.tipTitle}>{tip.title}</h4>
                    <p className={styles.tipDesc}>{tip.desc}</p>
                  </div>
                ))}
              </div>
            </div>
          )}

        </main>
      </div>

      {/* ── CTA BANNER ───────────────────────────────────── */}
      <section className={styles.ctaBanner}>
        <div className={styles.ctaBannerInner}>
          <h2 className={styles.ctaBannerTitle}>Ready to Start Practising?</h2>
          <p className={styles.ctaBannerSub}>80+ Free Mock Tests &amp; 12+ Premium categories — crafted by IIT/NIT &amp; Medical experts.</p>
          <div className={styles.ctaBannerBtns}>
            <Link to="/test-series/pyq" className={styles.ctaBannerBtn}>🎯 Try Free Tests</Link>
            <Link to="/test-series/jee-premium" className={styles.ctaBannerBtnSecondary}>⚡ JEE Premium</Link>
            <Link to="/test-series/neet-premium" className={styles.ctaBannerBtnSecondary}>🩺 NEET Premium</Link>
          </div>
        </div>
      </section>

      {/* ── FOOTER NOTE ──────────────────────────────────── */}
      <div className={styles.footerNote}>
        <p>© {currentYear} PrrepX Pathshala · All information is for {academicYear} academic cycle · Always verify with official sources</p>
      </div>

    </div>
  );
}
